import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { StageResultsTableService } from '../../services/stage-results-table.service';
import { GridBase } from 'src/app/controls/base/grid.base';
import { BreadcrumbComponent } from 'src/app/controls/breadcrumb/components/breadcrumb.component';
import { GridComponent } from 'src/app/controls/grid/components/grid.component';
import { ToolbarComponent } from 'src/app/controls/toolbar/components/toolbar.component';
import { ToolbarItemDirective } from 'src/app/controls/toolbar/directives/toolbar-item.directive';
import { TranslateModule } from '@ngx-translate/core';
import { GridButtonTrigger } from 'src/app/controls/grid/models/grid-button-trigger.interface';
import { StageCardService } from '../../../competition-management/edit-competition-card/edit-stage-card/service/stage-card.service';
import { StageResultList } from '../../models/stage-result-list.interface';
import { FormGroup } from '@angular/forms';
import { SpeedTrialForm } from '../../models/speed-trial-form.interface';
import { SpeedTrialResultComponent } from "../speed-trial-dialog/speed-trial-result.component";
import { StageBaseEntity } from '../../models/stage-base-entity.interface';
import { RadarComponent } from '../radar-dialog/radar.component';
import { ResultSummaryComponent } from 'src/app/controls/result-summary/components/result-summary.component';
import { ResultItemDirective } from 'src/app/controls/result-summary/directives/result-item.directive';
import { SpeedTrialService } from '../../services/speed-trial.service';
import { SpeedTrial } from '../../models/speed-trial.interface';
import { DialogComponent } from 'src/app/controls/dialog/components/dialog.component';
import { ResultsGridBase } from 'src/app/controls/base/results-grid.base';
import { CrewDetailComponent } from '../../../crew/components/crew-detail.component';
import { RoundResultsTableService } from '../../../round-result/services/round-results-table.service';
import { RoundService } from '../../../competition-management/add-competition-card/add-round-dialog/services/round.service';
import { CompetitionResultsTableService } from '../../../competition-result/services/competition-results-table.service';
import { CompetitionCardService } from '../../../competition-management/edit-competition-card/service/competition-card.service';

@Component({
  selector: 'app-stage-results-table',
  templateUrl: './stage-results-table.component.html',
  standalone: true,
  imports: [BreadcrumbComponent, GridComponent, ToolbarComponent, ToolbarItemDirective, TranslateModule, SpeedTrialResultComponent, RadarComponent, ResultSummaryComponent, ResultItemDirective, DialogComponent, CrewDetailComponent],
  providers: [StageResultsTableService, StageCardService, SpeedTrialService, RoundService, RoundResultsTableService, CompetitionCardService, CompetitionResultsTableService]
})
export class StageResultsTableComponent extends ResultsGridBase {

  /** Speed trial dialog reference */
  protected speedTrialDialog = viewChild.required<SpeedTrialResultComponent>(SpeedTrialResultComponent);

  /** Radar dialog reference */
  protected radarDialog = viewChild.required<RadarComponent>(RadarComponent);

  /** Crew detail dialog reference */
  protected crewDetailDialog = viewChild.required<CrewDetailComponent>(CrewDetailComponent);

  /** Competition formgroup reference */
  protected formGroup: FormGroup<SpeedTrialForm> | undefined = undefined;

  /** Stage card service reference */
  protected stageService = inject(StageCardService);

  /** Stage id reference */
  protected stageId: number | null = null;

  /** Speed trial reference */
  protected speedTrial: SpeedTrial | null = null;

  /** Competition id reference */
  private competitionId: number | null = null;

  /** Speed trial service reference */
  private speedTrialService = inject(SpeedTrialService);

  constructor(protected service: StageResultsTableService) {
    super(service);
  }

  ngOnInit(): void {
    /** initializes parent logic */
    super.ngOnInit();

    /** obtains ids from route */
    this.competitionId = this.route.parent.snapshot.params.id as number;
    this.stageId = this.route.snapshot.params.stageId as number;

    /** obtains display results status */
    this.loadDisplayResultsStatus(this.stageId, true);
  }

  /**
   * Displays the selected driving report
   * @param crewNumber the selected crew
   * @param drivingReportId the selected driving report id
   */
  private navigateToSpecificDrivingReport(
    crewId: number,
    drivingReportId: number
  ): void {
    this.router.navigate(['/competition', this.competitionId, 'stage', this.stageId, 'crew', crewId, 'driving-report', drivingReportId, 'display']);
  }

  /**
   * Navigates to speed trial results
   */
  private navigateToSpeedTrialResults(): void {
    this.router.navigate(['/competition', this.competitionId, 'stage', this.stageId, 'speed-trial', 'results']);
  }

  /**
   * Navigates to driving reports
   */
  private navigateToDrivingReports(): void {
    this.router.navigate(['/competition', this.competitionId, 'stage', this.stageId, 'driving-reports']);
  }

  /**
   * Navigates to the add driving report card
   */
  private createNewDrivingReport(): void {
    this.router.navigate(['/competition', this.competitionId, 'stage', this.stageId, 'driving-reports', 'add']);
  }

  /**
   * Grid action handler
   * @param event the clicked event from grid
   */
  protected onButtonClick(event: GridButtonTrigger<StageResultList>): void {
    /** initializes the stage properties */
    const stageProps: StageBaseEntity = {
      stageId: this.stageId,
      stageName: this.grid.getTableName
    }

    /** executes the button logic */
    switch (event.buttonId) {
      case 'show':
        this.navigateToSpecificDrivingReport(event.row.crewNumber, event.row.id);
        break;
      case 'speedTrial':
        /** if competitor is organizer, show edit speed trial dialog */
        if (this.isOrganizer()) {
          this.speedTrialDialog().show(event.row.id, stageProps);
        } else {
          /** otherwise show summary dialog with speed trial data */
          this.sub.add(this.speedTrialService.get(event.row.id).subscribe((result: SpeedTrial) => {
            this.speedTrial = result;
            /** waits for the dala load */
            setTimeout(() => {
              this.dialog.show();
            });
          }));
        }
        break;
      case 'radar':
        this.radarDialog().show(event.row.id, stageProps);
        break;
      case 'crewNumber':
        this.crewDetailDialog().show(event.row.crewNumber);
        break;
    }
  }

  /**
   * Toolbar click handler
   * @param itemId the clicked item
   */
  protected onToolbarClick(itemId: string): void {
    /** initializes the stage properties */
    const stageProps: StageBaseEntity = {
      stageId: this.stageId,
      stageName: this.grid.getTableName
    }
    
    /** executes the button logic */
    switch (itemId) {
      case 'recalculate':
        this.recalculateResults(this.stageId);
        break;
      case 'visibility':
        this.updateDisplayStatus(this.stageId);
        break;
      case 'print':
        this.print();
        break;
      case 'speed-trial':
        this.speedTrialDialog().show(undefined, stageProps);
        break;
      case 'radar':
        this.radarDialog().show(undefined, stageProps);
        break;
      case 'driving-report':
        this.createNewDrivingReport();
        break;
      case 'speed-trials':
        this.navigateToSpeedTrialResults();
        break;
      case 'driving-reports':
        this.navigateToDrivingReports();
        break;
    }
  }
}
